import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ButtonConfig = { label: string; url: string };

export type SectionData = {
  title: string | null;
  subtitle: string | null;
  description: string | null;
  secondary_description: string | null;
  image_url: string | null;
  mobile_image_url: string | null;
  secondary_image_url: string | null;
  mobile_secondary_image_url: string | null;
  buttons: ButtonConfig[];
};

function useSectionsQuery() {
  return useQuery({
    queryKey: ["site-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_sections")
        .select("*");
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useSectionData(sectionKey: string): SectionData & { isLoading: boolean } {
  const { data, isLoading } = useSectionsQuery();
  const section = data?.find((s) => s.section_key === sectionKey);

  let buttons: ButtonConfig[] = [];
  try {
    const raw = (section as any)?.buttons;
    if (Array.isArray(raw)) buttons = raw;
  } catch {}

  const image_url = section?.image_url ?? null;
  const mobile_image_url = section?.mobile_image_url ?? null;

  // Cache hero image URLs for next-visit LCP preload (see index.html bootstrap)
  useEffect(() => {
    if (sectionKey !== "hero" || typeof window === "undefined") return;
    if (image_url || mobile_image_url) {
      try {
        localStorage.setItem(
          "lpc:hero-preload",
          JSON.stringify({ desktop: image_url, mobile: mobile_image_url }),
        );
      } catch {}
    }
  }, [sectionKey, image_url, mobile_image_url]);

  return {
    isLoading,
    title: section?.title ?? null,
    subtitle: section?.subtitle ?? null,
    description: section?.description ?? null,
    secondary_description: section?.secondary_description ?? null,
    image_url,
    mobile_image_url,
    secondary_image_url: (section as any)?.secondary_image_url ?? null,
    mobile_secondary_image_url: section?.mobile_secondary_image_url ?? null,
    buttons,
  };
}

/** @deprecated Use useSectionData instead */
export function useSectionButtons(sectionKey: string): ButtonConfig[] {
  return useSectionData(sectionKey).buttons;
}
